/**
 * TaskCreateModal — criar/editar tarefa no detalhe da negociação.
 */
import React, { useState, useEffect } from 'react';
import { notify } from '../../../../utils/toast';
import { useTasks } from '../../../../contexts/tasks/TasksContext';
import { XIcon } from '../../../../components/icons';
import Input from '../../../../components/ui/Input';
import { Button } from '../../../../components/ui/Button';
import { createModuleLogger } from '../../../../utils/logger';
import { Task } from '../../../../types';

const log = createModuleLogger('[TaskCreate]');

interface TaskCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    leadId: string;
    assignedTo?: string;
    taskToEdit?: Task;
}

export const TaskCreateModal: React.FC<TaskCreateModalProps> = ({
    isOpen,
    onClose,
    leadId,
    assignedTo,
    taskToEdit,
}) => {
    const { createTask, updateTask } = useTasks();
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (isOpen && taskToEdit) {
            setTitle(taskToEdit.title);
            const dt = new Date(taskToEdit.dueDate);
            setDate(
                `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`,
            );
            setTime(
                `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`,
            );
        } else if (isOpen && !taskToEdit) {
            setTitle('');
            setDate('');
            setTime('');
        }
    }, [isOpen, taskToEdit]);

    const handleSubmit = async () => {
        if (!title || !date || !time) {
            notify.warning('Por favor, preencha todos os campos obrigatórios.');
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const [year, month, day] = date.split('-').map(Number);
            const [hours, minutes] = time.split(':').map(Number);
            const isoParams = {
                title,
                dueDate: new Date(year, month - 1, day, hours, minutes, 0, 0).toISOString(),
            };

            if (taskToEdit) {
                await updateTask({
                    ...taskToEdit,
                    ...isoParams,
                });
                notify.success('Tarefa atualizada com sucesso!');
            } else {
                await createTask({
                    ...isoParams,
                    type: 'Agendamento',
                    leadId,
                    isCompleted: false,
                    // @ts-ignore
                    assignedTo,
                });
                notify.success('Tarefa criada com sucesso!');
            }

            setTitle('');
            setDate('');
            setTime('');
            onClose();
        } catch (error) {
            log.error('Erro ao salvar tarefa:', error);
            notify.error('Erro ao salvar tarefa. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-s4 overlay-premium"
            onClick={onClose}
            role="presentation"
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="task-create-title"
                className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-subtle bg-surface-0 shadow-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between border-b border-subtle px-s5 py-s4">
                    <h3 id="task-create-title" className="text-heading-sm text-default">
                        {taskToEdit ? 'Editar Tarefa' : 'Nova Tarefa'}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex size-11 touch-manipulation items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-subtle hover:text-default md:size-9"
                        aria-label="Fechar"
                    >
                        <XIcon className="size-5" aria-hidden />
                    </button>
                </header>

                <div className="space-y-s4 p-s5">
                    <Input
                        label="Nome da tarefa"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Ex: Ligar para confirmar reunião"
                        autoFocus
                    />

                    <div className="grid grid-cols-2 gap-s4">
                        <Input
                            label="Data"
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="font-mono"
                        />
                        <Input
                            label="Horário"
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="font-mono"
                        />
                    </div>
                </div>

                <footer className="flex justify-end gap-s3 border-t border-subtle bg-surface-subtle px-s5 py-s4">
                    <Button variant="outline" onClick={onClose} className="min-h-11 touch-manipulation">
                        Cancelar
                    </Button>
                    <Button
                        variant="default"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="min-h-11 touch-manipulation"
                    >
                        {isSubmitting ? 'Salvando...' : taskToEdit ? 'Salvar' : 'Criar tarefa'}
                    </Button>
                </footer>
            </div>
        </div>
    );
};
